import { computed, nextTick, onMounted, onUnmounted, readonly, ref, type StyleValue, type UnwrapNestedRefs, watch } from 'vue'

import { IS_SAFARI } from '@/constants/app'
import { getAttachElm, type MenuAttach } from '@/utils/element'

export type MenuPosition = 'left' | 'right' | 'center' | 'left-top' | 'right-top' | 'center-end'
export type MenuOffset = 'x' | 'y'

interface Props {
  position?: MenuPosition
  positionX?: number
  positionY?: number
  offset?: MenuOffset
  attach: MenuAttach
  isCheckMenuContent?: boolean
  disabled?: boolean
  clickEnabled?: boolean
  hoverEnabled?: boolean
  isFixedMinWidth?: boolean
  isFixedMaxWidth?: boolean
  relateMenuContents?: string[]
  hoverDelay?: number
  isCheckSafariResize?: boolean // special case
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function useMenu (props: UnwrapNestedRefs<Props>) {
  const activatorRootEml = ref<HTMLDivElement | null>(null)
  const activatorInputEml = ref<HTMLInputElement | null>(null)
  const resizeObserver = ref<ResizeObserver | null>(null)
  const isFocus = ref(false)
  const menuContent = ref<HTMLDivElement | null>(null)
  const menuContentStyle = ref<StyleValue>()
  const isActivatorHover = ref(false)
  const isMenuContentHover = ref(false)
  const clearActivatorHoverTimeout = ref<NodeJS.Timeout | undefined>(undefined)
  const clearMenuContentHoverTimeout = ref<NodeJS.Timeout | undefined>(undefined)

  const position = computed((): MenuPosition => props.position ?? 'left')
  const offset = computed((): MenuOffset => props.offset ?? 'y')
  const positionX = computed((): number => props.positionX ?? 0)
  const positionY = computed((): number => props.positionY ?? 0)
  const isCheckMenuContent = computed((): boolean => props.isCheckMenuContent ?? false)
  const disabled = computed((): boolean => props.disabled ?? false)
  const clickEnabled = computed((): boolean => props.clickEnabled ?? true)
  const hoverEnabled = computed((): boolean => props.hoverEnabled ?? false)
  const isFixedMinWidth = computed((): boolean => props.isFixedMinWidth ?? true)
  const isFixedMaxWidth = computed((): boolean => props.isFixedMaxWidth ?? false)
  const relateMenuContents = computed((): string[] => props.relateMenuContents ?? [])
  const isCheckSafariResize = computed((): boolean => props.isCheckSafariResize ?? false)

  const isHover = computed(() => isActivatorHover.value || isMenuContentHover.value)

  watch(() => isFocus.value, () => {
    if (isFocus.value) {
      handleSetMenuContentStyle()
      setTimeout(() => activatorInputEml.value?.focus(), 0)
    }
  })

  watch(() => isHover.value, async () => {
    menuContent.value?.removeEventListener('mouseover', handleMenuContentMouseover)
    menuContent.value?.removeEventListener('mouseleave', handleMenuContentMouseleave)

    if (isHover.value) {
      handleSetMenuContentStyle()
      nextTick(() => {
        handleResizeObserver()
        menuContent.value?.addEventListener('mouseover', handleMenuContentMouseover)
        menuContent.value?.addEventListener('mouseleave', handleMenuContentMouseleave)
      })
    }
  })

  watch(() => disabled.value, async () => {
    if (hoverEnabled.value) {
      isActivatorHover.value = false
      isMenuContentHover.value = false
      clearTimeout(clearActivatorHoverTimeout.value)
    }

    if (disabled.value) {
      nextTick(() => {
        activatorRootEml.value?.removeEventListener('mouseover', handleMouseover)
        activatorRootEml.value?.removeEventListener('mouseleave', handleMouseleave)
      })
    } else {
      nextTick(() => {
        if (hoverEnabled.value) {
          activatorRootEml.value?.addEventListener('mouseover', handleMouseover)
          activatorRootEml.value?.addEventListener('mouseleave', handleMouseleave)
        }
      })
    }
  })

  const handleSetMenuContentStyle = (): void => {
    if (activatorRootEml.value === null) {
      menuContentStyle.value = {}
      return
    }

    const attachElm = getAttachElm(props.attach)

    if (attachElm === null) {
      return
    }

    const activatorRootEmlRect = activatorRootEml.value.getBoundingClientRect()

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const pageYOffset = attachElm.pageYOffset || 0
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const pageXOffset = attachElm.pageXOffset || 0

    let top = activatorRootEmlRect.top + pageYOffset
    top += activatorRootEml.value.offsetHeight
    let left = activatorRootEmlRect.left + pageXOffset

    menuContentStyle.value = {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`
    }

    if (isFixedMinWidth.value) {
      menuContentStyle.value.minWidth = `${activatorRootEmlRect.width}px`
    }

    if (isFixedMaxWidth.value) {
      menuContentStyle.value.maxWidth = `${activatorRootEmlRect.width}px`
    }

    nextTick(() => {
      if (menuContent.value === null) {
        return
      }

      const menuContentElmRect = menuContent.value.getBoundingClientRect()

      let menuContentElmRectRight = menuContentElmRect.right
      let menuContentElmRectBottom = menuContentElmRect.bottom

      if (position.value === 'left-top' || position.value === 'right-top') {
        top -= menuContentElmRect.height + activatorRootEml.value!.offsetHeight
        menuContentElmRectBottom -= menuContentElmRect.height + activatorRootEml.value!.offsetHeight

        if (offset.value === 'x') {
          top += activatorRootEmlRect.height
          menuContentElmRectBottom += activatorRootEmlRect.height
        }
      } else if (offset.value === 'x') {
        top -= activatorRootEmlRect.height
        menuContentElmRectBottom -= activatorRootEmlRect.height
      }

      if (position.value === 'right' || position.value === 'right-top') {
        left = left - menuContentElmRect.width + activatorRootEmlRect.width
        menuContentElmRectRight = menuContentElmRectRight - menuContentElmRect.width + activatorRootEmlRect.width

        if (offset.value === 'x') {
          left += menuContentElmRect.width
          menuContentElmRectRight += menuContentElmRect.width
        }
      } else {
        if (offset.value === 'x') {
          left -= activatorRootEmlRect.width
          menuContentElmRectRight -= activatorRootEmlRect.width
        }

        if (position.value === 'center') {
          left = left - menuContentElmRect.width / 2 + activatorRootEmlRect.width / 2
          menuContentElmRectRight = menuContentElmRectRight - menuContentElmRect.width / 2 + activatorRootEmlRect.width / 2
        }

        if (position.value === 'center-end') {
          left = left + activatorRootEmlRect.width
          menuContentElmRectRight = menuContentElmRectRight + activatorRootEmlRect.width
          top -= (activatorRootEmlRect.height - activatorRootEmlRect.height / 2 + menuContentElmRect.height / 2)
          menuContentElmRectBottom -= (activatorRootEmlRect.height - activatorRootEmlRect.height / 2 + menuContentElmRect.height / 2)
        }
      }

      if (menuContentElmRectBottom + positionY.value > window.innerHeight) {
        top -= menuContentElmRect.height + activatorRootEml.value!.offsetHeight
        if (offset.value === 'y') {
          top += positionY.value
        } else {
          top += activatorRootEmlRect.height * 2
        }
      } else if (offset.value === 'y' && (position.value === 'left-top' || position.value === 'right-top')) {
        top -= positionY.value
      } else if (offset.value === 'y') {
        top += positionY.value
      }

      if (menuContentElmRectRight + positionX.value > window.innerWidth) {
        left = left - menuContentElmRect.width + activatorRootEml.value!.offsetWidth - positionX.value

        if (left < 0) {
          left = 8
        }
      } else if (left - positionX.value < 0) {
        left = activatorRootEmlRect.left + pageXOffset + positionX.value

        if (left > window.innerWidth) {
          left = window.innerWidth - 8
        }
      } else if (offset.value === 'x' && (position.value === 'left' || position.value === 'left-top')) {
        left -= (menuContentElmRect.width - activatorRootEmlRect.width + positionX.value)
      } else if (offset.value === 'x') {
        left += positionX.value
      }

      menuContentStyle.value = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`
      }

      if (isFixedMinWidth.value) {
        menuContentStyle.value.minWidth = `${activatorRootEmlRect.width}px`
      }

      if (isFixedMaxWidth.value) {
        menuContentStyle.value.maxWidth = `${activatorRootEmlRect.width}px`
      }
    })
  }

  const checkContainsRelateMenuContents = (target: HTMLElement | null): boolean => {
    return relateMenuContents.value.findIndex((relateMenuContent) => {
      return document.getElementById(relateMenuContent)?.contains(target) === true
    }) > -1
  }

  const handleFocusAndBlur = (e: Event): void => {
    if (disabled.value) {
      return
    }

    const target = e.target as (HTMLElement | null)

    if (isCheckSafariResize.value && hoverEnabled.value && IS_SAFARI && activatorRootEml.value?.contains(target)) {
      nextTick(() => {
        const mouseEvent = e as MouseEvent
        setTimeout(() => {
          isActivatorHover.value = !!activatorRootEml.value?.contains(
            document.elementFromPoint(mouseEvent.clientX, mouseEvent.clientY))
        }, 350)
      })
    }

    if (!clickEnabled.value) {
      return
    }

    if (isFocus.value &&
      (activatorInputEml.value?.contains(target) ??
      (activatorInputEml.value !== null && activatorRootEml.value?.contains(target)))) {
      return
    }

    isFocus.value = activatorRootEml.value?.contains(target)
      ? !isFocus.value
      : (isCheckMenuContent.value && menuContent.value?.contains(target)) ??
      (isFocus.value && checkContainsRelateMenuContents(target))
  }

  const handleMouseover = (): void => {
    if (disabled.value) {
      return
    }

    isActivatorHover.value = true
    clearTimeout(clearActivatorHoverTimeout.value)
  }

  const handleMouseleave = (): void => {
    if (disabled.value) {
      return
    }

    if (props.hoverDelay === undefined) {
      isActivatorHover.value = false
      return
    }

    clearActivatorHoverTimeout.value = setTimeout(() => (isActivatorHover.value = false), props.hoverDelay)
  }

  const handleMenuContentMouseover = (): void => {
    isMenuContentHover.value = true
    clearTimeout(clearMenuContentHoverTimeout.value)
  }

  const handleMenuContentMouseleave = (): void => {
    if (props.hoverDelay === undefined) {
      isMenuContentHover.value = false
      return
    }

    clearMenuContentHoverTimeout.value = setTimeout(() => (isMenuContentHover.value = false), props.hoverDelay)
  }

  const setIsFocus = (value: boolean): void => {
    isFocus.value = value
  }

  onUnmounted(() => {
    window.removeEventListener('click', handleFocusAndBlur)

    if (hoverEnabled.value) {
      activatorRootEml.value?.removeEventListener('mouseover', handleMouseover)
      activatorRootEml.value?.removeEventListener('mouseleave', handleMouseleave)
    }

    window.removeEventListener('scroll', handleScroll, true)
  })

  window.addEventListener('click', handleFocusAndBlur)

  onMounted(() => {
    if (hoverEnabled.value && !disabled.value) {
      activatorRootEml.value?.addEventListener('mouseover', handleMouseover)
      activatorRootEml.value?.addEventListener('mouseleave', handleMouseleave)
    }
  })

  const handleScroll = (): void => {
    if (disabled.value) {
      return
    }

    handleSetMenuContentStyle()
  }

  const handleResizeObserver = (): void => {
    menuContent.value && resizeObserver.value?.unobserve(menuContent.value)

    resizeObserver.value = new ResizeObserver(() => {
      if (menuContent.value === null) {
        return
      }

      handleSetMenuContentStyle()
    })

    menuContent.value && resizeObserver.value?.observe(menuContent.value)
  }

  window.addEventListener('scroll', handleScroll, true)

  return {
    activatorRootEml,
    activatorInputEml,
    menuContent,
    menuContentStyle,
    isFocus: readonly(isFocus),
    isHover: readonly(isHover),
    setIsFocus,
    handleSetMenuContentStyle
  }
}
